import { queryOptions } from "@tanstack/react-query";
import { ApiError, api } from "./api.ts";

/** `demo`: the hub runs the public demo, which visitors can enter without an account. */
export type AuthState = { needsSetup: boolean; demo?: boolean };
/** `visitor`: a public demo visit, which is read-only. */
export type SessionUser = { id: string; email: string; visitor?: boolean };
export type SessionResponse = {
  user: SessionUser;
  session: { id: string; createdAt: number; expiresAt: number };
};
export type SessionListItem = {
  id: string;
  createdAt: number;
  lastSeenAt: number;
  expiresAt: number;
  ip: string;
  userAgent: string;
  current: boolean;
};
export type SetupStartResponse = { enrollmentId: string; secret: string; otpauthUri: string };

export const authApi = {
  state: () => api<AuthState>("/api/auth/state"),
  session: () => api<SessionResponse>("/api/auth/session"),
  setupStart: (body: { setupToken: string; email: string; password: string }) =>
    api<SetupStartResponse>("/api/auth/setup/start", { method: "POST", body }),
  setupFinish: (body: { enrollmentId: string; code: string }) =>
    api<{ user: SessionUser; recoveryCodes: string[] }>("/api/auth/setup/finish", {
      method: "POST",
      body,
    }),
  login: (body: { email: string; password: string }) =>
    api<{ stage: "totp" }>("/api/auth/login", { method: "POST", body }),
  totp: (code: string) =>
    api<{ user: SessionUser }>("/api/auth/totp", { method: "POST", body: { code } }),
  logout: () => api<{ ok: true }>("/api/auth/logout", { method: "POST", body: {} }),
  demo: () => api<{ user: SessionUser }>("/api/auth/demo", { method: "POST", body: {} }),
  sessions: () => api<SessionListItem[]>("/api/auth/sessions"),
  revokeSession: (id: string) =>
    api<void>(`/api/auth/sessions/${encodeURIComponent(id)}`, { method: "DELETE" }),
};

export const authStateQuery = queryOptions({
  queryKey: ["auth", "state"],
  queryFn: authApi.state,
  staleTime: 30_000,
});
export const sessionQuery = queryOptions({
  queryKey: ["auth", "session"],
  queryFn: authApi.session,
  staleTime: 60_000,
  retry: false,
});
/** Not under ["auth"]: a 401 here means the session ended, like any data query. */
export const sessionsQuery = queryOptions({ queryKey: ["sessions"], queryFn: authApi.sessions });

function waitText(seconds: number): string {
  if (seconds >= 120) return `${Math.ceil(seconds / 60)} minutes`;
  if (seconds >= 60) return "1 minute";
  return `${seconds} seconds`;
}

export function authErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.code) {
      case "invalid_credentials":
        return "Email or password is incorrect.";
      case "bad_code":
        return "That code didn't match. Check your authenticator app and try again.";
      case "bad_setup_token":
        return "That setup token isn't valid. Copy it again from the hub log.";
      case "weak_password":
        return err.message.endsWith(".") ? err.message : `${err.message}.`;
      case "locked":
        return err.retryAfterS
          ? `Too many failed attempts. Try again in ${waitText(err.retryAfterS)}.`
          : "Too many failed attempts. Try again later.";
      case "no_pending_login":
        return "Your sign-in timed out. Enter your password again.";
      case "enrollment_expired":
        return "Setup took too long. Start again from the first step.";
      case "already_set_up":
        return "The admin account already exists. Sign in instead.";
      case "read_only_demo":
        return "The demo is read-only.";
      default:
        if (err.status === 429) return "Too many attempts. Wait a minute and try again.";
        return "The hub hit an error. Try again in a moment.";
    }
  }
  return "Can't reach the hub. Check that it is running.";
}
